import type { ScanPartContext, ScanRootOptimized } from '@kaiizen/kai-scan-template-system';
import { useLayoutEffect, useRef, useState } from 'react';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { useConfirmationModal, useToast } from '@/Client/State';
import { styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { styleguideFontFamily } from '@kaiizen/kai-shiki/core';
import { CommonActionButton, GallerySelectionActionBar } from '@kaiizen/kai-shiki/react';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { GalleryMoveCopyPicker } from './GalleryMoveCopyPicker';
import {
  applyGalleryPhotoOperation,
  type GalleryPhotoOccurrence,
  type GalleryPhotoOperation,
  type GalleryPhotoTarget,
} from './galleryPhotoOperations';

export type GalleryPhotoManagementContract = {
  scanRoot: ScanRootOptimized;
  targets: readonly GalleryPhotoTarget[];
  applyPhotoOperation: (operation: GalleryPhotoOperation) => void | Promise<void>;
};

export const getGalleryOperationToast = (
  count: number,
  operation: GalleryPhotoOperation['type'],
): string =>
  operation === 'setDate'
    ? `${count} photo date${count === 1 ? '' : 's'} updated`
    : `${count} photo assignment${count === 1 ? '' : 's'} ${operation === 'remove' ? 'removed' : operation === 'move' ? 'moved' : 'copied'}`;

const ACTION_STYLE = {
  minHeight: 32,
  border: '1px solid rgba(255,255,255,.35)',
  borderRadius: 0,
  background: 'transparent',
  color: styleguideColors.white,
  fontFamily: styleguideFontFamily.mono,
  fontSize: pxToRem(11),
  letterSpacing: '0.04em',
} as const;

const DESTRUCTIVE_ACTION_STYLE = {
  ...ACTION_STYLE,
  borderColor: 'transparent',
} as const;

const PRIMARY_ACTION_STYLE = {
  ...ACTION_STYLE,
  borderColor: styleguideColors.accent,
  background: styleguideColors.accent,
  color: styleguideColors.ink,
} as const;

const DisabledGalleryPhotoManagement = ({
  selectedCount,
  clearSelection,
  selectAllVisible,
}: {
  selectedCount: number;
  clearSelection: () => void;
  selectAllVisible: () => void;
}) => (
  <GallerySelectionActionBar
    selectedCount={selectedCount}
    actions={
      <>
        <CommonActionButton style={ACTION_STYLE} onClick={selectAllVisible}>Select all visible</CommonActionButton>
        <CommonActionButton style={ACTION_STYLE} onClick={clearSelection}>Clear selection</CommonActionButton>
        <CommonActionButton style={DESTRUCTIVE_ACTION_STYLE} disabled>Remove selected</CommonActionButton>
        <CommonActionButton style={PRIMARY_ACTION_STYLE} disabled>Move or copy selected</CommonActionButton>
      </>
    }
  />
);

const ActiveGalleryPhotoManagement = ({
  management,
  selectedOccurrences,
  clearSelection,
  selectAllVisible,
  scanIsEditable,
}: {
  management: GalleryPhotoManagementContract;
  selectedOccurrences: ReadonlyMap<string, GalleryPhotoOccurrence>;
  clearSelection: () => void;
  selectAllVisible: () => void;
  scanIsEditable: boolean;
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const confirm = useConfirmationModal();
  const { showToast, showErrorToast } = useToast();
  const currentRef = useRef({
    active: true,
    management,
    selectedOccurrences,
    clearSelection,
    scanIsEditable,
  });
  const sources = [...selectedOccurrences.values()];

  useLayoutEffect(() => {
    currentRef.current = {
      active: true,
      management,
      selectedOccurrences,
      clearSelection,
      scanIsEditable,
    };
  });
  useLayoutEffect(() => () => {
    currentRef.current.active = false;
  }, []);

  const applyOperation = async (
    type: 'remove' | 'move' | 'copy',
    target?: ScanPartContext,
  ) => {
    const current = currentRef.current;
    const currentSources = [...current.selectedOccurrences.values()];
    if (!current.active || !current.scanIsEditable || !currentSources.length) return false;
    let operation: GalleryPhotoOperation;
    if (type === 'remove') operation = { type, sources: currentSources };
    else {
      if (!target) return false;
      operation = { type, sources: currentSources, target };
    }
    try {
      await current.management.applyPhotoOperation(operation);
      trackEvent(
        operation.type === 'move'
          ? ANALYTICS_EVENTS.scanPhotosMoved
          : operation.type === 'copy'
          ? ANALYTICS_EVENTS.scanPhotosCopied
          : ANALYTICS_EVENTS.scanPhotoAssignmentsRemoved,
        {
          scanId: current.management.scanRoot.id,
          photoCount: currentSources.length,
          source: 'gallery',
        },
      );
      current.clearSelection();
      showToast(getGalleryOperationToast(currentSources.length, operation.type));
      return true;
    } catch (error) {
      showErrorToast(error, `${operation.type} selected photo assignments`);
      return false;
    }
  };
  const remove = () =>
    confirm({
      title: 'Remove photo assignments',
      message: `Remove ${sources.length} selected photo assignment${sources.length === 1 ? '' : 's'}? Other uses of ${
        sources.length === 1 ? 'this photo' : 'these photos'
      } will remain available.`,
      confirmLabel: 'Remove',
      onConfirm: () => applyOperation('remove'),
    });
  const getMoveCopyOperation = (
    type: 'move' | 'copy',
    target: ScanPartContext,
  ): GalleryPhotoOperation => ({ type, sources, target });

  return (
    <>
      {pickerOpen
        ? (
          <GalleryMoveCopyPicker
            selectedCount={sources.length}
            targets={management.targets}
            canApply={(type, target) => (
              scanIsEditable && applyGalleryPhotoOperation(
                  management.scanRoot,
                  getMoveCopyOperation(type, target),
                ) !== management.scanRoot
            )}
            onApply={async (type, target) => {
              await applyOperation(type, target);
            }}
            onClose={() => setPickerOpen(false)}
          />
        )
        : null}
      <GallerySelectionActionBar
        selectedCount={sources.length}
        actions={
          <>
            <CommonActionButton style={ACTION_STYLE} onClick={selectAllVisible}>Select all visible</CommonActionButton>
            <CommonActionButton style={ACTION_STYLE} onClick={clearSelection}>Clear selection</CommonActionButton>
            <CommonActionButton style={DESTRUCTIVE_ACTION_STYLE} onClick={remove}>Remove selected</CommonActionButton>
            <CommonActionButton style={PRIMARY_ACTION_STYLE} onClick={() => setPickerOpen(true)}>Move or copy selected</CommonActionButton>
          </>
        }
      />
    </>
  );
};

/** Owns optional bulk-management controls and authorization for the active Gallery selection. */
export const GalleryPhotoManagement = ({
  management,
  selectedOccurrences,
  clearSelection,
  selectAllVisible,
  scanIsEditable,
}: {
  management?: GalleryPhotoManagementContract;
  selectedOccurrences: ReadonlyMap<string, GalleryPhotoOccurrence>;
  clearSelection: () => void;
  selectAllVisible: () => void;
  scanIsEditable: boolean;
}) =>
  management
    ? (
      <ActiveGalleryPhotoManagement
        management={management}
        selectedOccurrences={selectedOccurrences}
        clearSelection={clearSelection}
        selectAllVisible={selectAllVisible}
        scanIsEditable={scanIsEditable}
      />
    )
    : (
      <DisabledGalleryPhotoManagement
        selectedCount={selectedOccurrences.size}
        clearSelection={clearSelection}
        selectAllVisible={selectAllVisible}
      />
    );
